import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import SearchIcon from '@mui/icons-material/Search'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { apiClient } from '../../services/api'
import { getErrorMessage } from '../../services/core/errorHandler'
import { poolwerxColors } from '../../theme/colors'
import {
  getResourceIcon,
  getResourceShortLabel,
  resourceTypeSortKey,
} from '../../utils/resourceIcons'
import { buildResourceUrl } from '../../utils/resourceUrls'
import type {
  FranchisePartnerResource,
  ResourceStatus,
  ResourceType,
} from '../../types/resources'
import { ADMIN_TABLE_HEADER_SX, inlineActionIconSx } from './adminTableStyles'

interface FpResourcesTableProps {
  fpId: number
  resources: FranchisePartnerResource[]
  onResourcesChanged: (next: FranchisePartnerResource[]) => void
}

// Mirrors backend ``shared.resource_catalog.RESOURCE_ID_PATTERN``.
// Google Drive folder + Sheet IDs share the same alphabet/length envelope.
const RESOURCE_ID_REGEX = /^[A-Za-z0-9_-]{20,128}$/
const ID_FORMAT_HINT = "ID must be 20-128 chars (letters, digits, '-', '_')"

const STATUS_COLORS: Record<ResourceStatus, { bg: string; fg: string; border: string; label: string }> = {
  linked: { bg: '#E8F5E9', fg: '#2E7D32', border: '#A5D6A7', label: 'Linked' },
  missing: { bg: '#F5F5F5', fg: '#616161', border: '#E0E0E0', label: 'Missing' },
  invalid: { bg: '#FFEBEE', fg: '#C62828', border: '#EF9A9A', label: 'Invalid' },
}

const formatRelative = (iso?: string | null): string | null => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const diffMs = Date.now() - d.getTime()
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return d.toLocaleDateString()
}

const sourceCaption = (source?: string | null): string => {
  if (source === 'register') return 'from register'
  if (source === 'manual') return 'manual'
  return ''
}

const computeStatus = (resourceId: string | null, resourceUrl: string | null | undefined): ResourceStatus => {
  if (resourceUrl) return 'linked'
  if (!resourceId) return 'missing'
  if (!RESOURCE_ID_REGEX.test(resourceId)) return 'invalid'
  return 'linked'
}

const PlatformChip: React.FC<{ resourceType: ResourceType }> = ({ resourceType }) => {
  const Icon = getResourceIcon(resourceType)
  const label = getResourceShortLabel(resourceType)
  return (
    <Chip
      size="small"
      icon={<Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5 }}><Icon size={16} /></Box>}
      label={label}
      sx={{ height: 24, fontSize: '0.75rem' }}
      variant="outlined"
    />
  )
}

const StatusChip: React.FC<{ status: ResourceStatus }> = ({ status }) => {
  const c = STATUS_COLORS[status]
  return (
    <Chip
      size="small"
      label={c.label}
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        color: c.fg,
        backgroundColor: c.bg,
        border: `1px solid ${c.border}`,
        '& .MuiChip-label': { px: 0.8 },
      }}
    />
  )
}

const MetaCell: React.FC<{ meta?: Record<string, unknown> | null }> = ({ meta }) => {
  const entries = meta ? Object.entries(meta) : []
  if (!entries.length) {
    return <Box sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>-</Box>
  }
  const tooltip = (
    <Box sx={{ p: 0.5, fontSize: '0.75rem' }}>
      {entries.map(([k, v]) => (
        <Box key={k} sx={{ display: 'flex', gap: 1, lineHeight: 1.5 }}>
          <Box sx={{ fontWeight: 600, color: '#fff' }}>{k}:</Box>
          <Box sx={{ wordBreak: 'break-word' }}>{v === '' || v == null ? '-' : String(v)}</Box>
        </Box>
      ))}
    </Box>
  )
  return (
    <Tooltip title={tooltip} arrow placement="left">
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ cursor: 'default' }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {entries.length} {entries.length === 1 ? 'field' : 'fields'}
        </Typography>
      </Stack>
    </Tooltip>
  )
}

const ResourceRow: React.FC<{
  resource: FranchisePartnerResource
  fpId: number
  onChange: (next: FranchisePartnerResource) => void
}> = ({ resource, fpId, onChange }) => {
  // The TextField is fully controlled by `resource.resource_id` so every
  // keystroke flows straight to the form's `formData.resources`. That
  // keeps `isDirty` in sync with what the user is actually typing, so
  // the unsaved-changes guard fires the moment a key has been hit
  // (rather than waiting for blur).
  const [lookupRunning, setLookupRunning] = useState(false)
  const [rowError, setRowError] = useState<string | null>(null)
  const [rowMessage, setRowMessage] = useState<string | null>(null)

  const propagate = (incomingId: string | null) => {
    onChange({
      ...resource,
      resource_id: incomingId,
      url: buildResourceUrl(resource.resource_type, incomingId),
      status: computeStatus(incomingId, resource.resource_url ?? null),
    })
  }

  const handleIdChange = (raw: string) => {
    setRowError(null)
    setRowMessage(null)
    propagate(raw === '' ? null : raw)
  }

  const handleBlur = () => {
    const current = resource.resource_id ?? ''
    const trimmed = current.trim()
    if (trimmed && !RESOURCE_ID_REGEX.test(trimmed)) {
      setRowError(ID_FORMAT_HINT)
      return
    }
    if (trimmed !== current) {
      propagate(trimmed || null)
    }
  }

  const lookup = async () => {
    setLookupRunning(true)
    setRowError(null)
    setRowMessage(null)
    try {
      const response = await apiClient.post(
        `/admin/franchise-partners/${fpId}/resources/${resource.resource_code}/lookup-from-register`,
      )
      const hit = response.data as { found: boolean; id: string | null }
      setLookupRunning(false)
      if (hit.found && hit.id) {
        propagate(hit.id)
        setRowMessage('Populated from register - press the Save Changes button below to save')
      } else {
        setRowMessage('Not in register')
      }
    } catch (err) {
      setLookupRunning(false)
      setRowError(getErrorMessage(err) || 'Lookup failed')
    }
  }

  const copyUrl = () => {
    if (!resource.url) return
    void navigator.clipboard.writeText(resource.url).then(() => {
      setRowMessage('Copied URL')
    })
  }

  const checkedRelative = formatRelative(resource.last_checked_at ?? resource.last_synced_at)
  const sourceLabel = sourceCaption(resource.last_sync_source)

  const inlineActions = (
    <InputAdornment position="end">
      <Stack direction="row" spacing={0.25}>
        <Tooltip title={resource.url ? 'Open in new tab' : 'Enter an ID to enable'}>
          <span>
            <IconButton
              size="small"
              component="a"
              href={resource.url || undefined}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!resource.url}
              sx={inlineActionIconSx(poolwerxColors.teal)}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={resource.url ? 'Copy URL' : 'No URL to copy'}>
          <span>
            <IconButton
              size="small"
              onClick={copyUrl}
              disabled={!resource.url}
              sx={inlineActionIconSx(poolwerxColors.teal)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {resource.register_eligible && (
          <Tooltip title="Look up from PSC register sheet">
            <span>
              <IconButton
                size="small"
                onClick={lookup}
                disabled={lookupRunning}
                sx={inlineActionIconSx(poolwerxColors.teal)}
              >
                {lookupRunning ? (
                  <CircularProgress size={14} />
                ) : (
                  <SearchIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </InputAdornment>
  )

  return (
    <TableRow hover>
      <TableCell sx={{ verticalAlign: 'top', pt: 1.25 }}>
        <Tooltip title={resource.description} arrow placement="top-start">
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, cursor: 'help', display: 'inline-block' }}
          >
            {resource.label}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top', pt: 1.25 }}>
        <PlatformChip resourceType={resource.resource_type} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top', pt: 0.75, minWidth: 360 }}>
        <TextField
          size="small"
          fullWidth
          value={resource.resource_id ?? ''}
          onChange={e => handleIdChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="Resource ID"
          InputProps={{ endAdornment: inlineActions }}
        />
        {rowError && (
          <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.25 }}>
            {rowError}
          </Typography>
        )}
        {rowMessage && !rowError && (
          <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 0.25 }}>
            {rowMessage}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top', pt: 1.25 }}>
        <StatusChip status={resource.status} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top', pt: 1.25 }}>
        <MetaCell meta={resource.meta} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top', pt: 1.25, fontSize: '0.75rem', color: 'text.secondary' }}>
        {checkedRelative ? (
          <>
            <Box>{checkedRelative}</Box>
            {sourceLabel && <Box sx={{ fontSize: '0.7rem' }}>{sourceLabel}</Box>}
          </>
        ) : (
          <Box>-</Box>
        )}
      </TableCell>
    </TableRow>
  )
}

const FpResourcesTable: React.FC<FpResourcesTableProps> = ({
  fpId,
  resources,
  onResourcesChanged,
}) => {
  const sortedResources = useMemo(
    () =>
      [...resources].sort(
        (a, b) => resourceTypeSortKey(a.resource_type) - resourceTypeSortKey(b.resource_type),
      ),
    [resources],
  )

  const handleRowChange = (next: FranchisePartnerResource) => {
    const updated = sortedResources.map(r =>
      r.resource_code === next.resource_code ? next : r,
    )
    onResourcesChanged(updated)
  }

  if (!sortedResources.length) {
    return (
      <Alert severity="info">
        No resource types configured. Edit the catalog at
        backend/shared/resource_catalog.py to add one.
      </Alert>
    )
  }

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={ADMIN_TABLE_HEADER_SX}>Resource</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Platform</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Link ID / URL</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Status</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Meta</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Last checked</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedResources.map(resource => (
            <ResourceRow
              key={resource.resource_code}
              resource={resource}
              fpId={fpId}
              onChange={handleRowChange}
            />
          ))}
        </TableBody>
      </Table>
      </TableContainer>
    </Box>
  )
}

export default FpResourcesTable
